import React, { useEffect, useState } from "react";
import { Autocomplete, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export type DerivedAttributeValueType = "BOOLEAN" | "NUMBER" | "STRING";

function parseLiteral(input: string): unknown {
    const trimmed = input.trim();
    if (trimmed.toLowerCase() === "true") return true;
    if (trimmed.toLowerCase() === "false") return false;
    const n = Number(trimmed);
    if (trimmed !== "" && !Number.isNaN(n)) return n;
    return trimmed;
}

function formatLiteral(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "number") return String(value);
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function parseByType(input: string, valueType?: DerivedAttributeValueType): unknown {
    if (!valueType || valueType === "STRING") return parseLiteral(input);
    if (valueType === "BOOLEAN") {
        const t = input.trim().toLowerCase();
        if (t === "true") return true;
        if (t === "false") return false;
        return undefined;
    }
    if (valueType === "NUMBER") {
        const n = Number(input.trim());
        return input.trim() !== "" && !Number.isNaN(n) ? n : undefined;
    }
    return parseLiteral(input);
}

export interface DerivedAttributeValueInputProps {
    value: unknown;
    onChange: (value: unknown) => void;
    /** When set, restricts input/parsing to this type (boolean dropdown, number input, or string). */
    valueType?: DerivedAttributeValueType;
    /** Existing values for this derived attribute to show in the dropdown when focused. */
    existingValues?: unknown[];
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    fullWidth?: boolean;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    "data-testid"?: string;
}

/**
 * Input for derived attribute value. When valueType is set, UI is scoped (boolean select, number field, or string).
 * Otherwise shows autocomplete with existing values; user can select or type and press Enter.
 */
export const DerivedAttributeValueInput: React.FC<DerivedAttributeValueInputProps> = ({
    value,
    onChange,
    valueType,
    existingValues = [],
    label = "Value",
    placeholder = "Select or type value and press Enter",
    size = "small",
    fullWidth = true,
    disabled = false,
    sx,
    "data-testid": dataTestId,
}) => {
    const displayStr = formatLiteral(value);
    const [localInput, setLocalInput] = useState(displayStr);

    useEffect(() => {
        setLocalInput(formatLiteral(value));
    }, [value]);

    const options = existingValues.map((v) => ({ raw: v, display: formatLiteral(v) }));
    const uniqueByDisplay = options.filter(
        (opt, i, arr) => arr.findIndex((o) => o.display === opt.display) === i
    );

    const commit = (inputValue: string) => {
        const parsed = valueType ? parseByType(inputValue, valueType) : parseLiteral(inputValue);
        if (parsed !== undefined) {
            onChange(parsed);
            setLocalInput(formatLiteral(parsed));
        }
    };

    if (valueType === "BOOLEAN") {
        return (
            <FormControl size={size} fullWidth={fullWidth} disabled={disabled} sx={sx}>
                <InputLabel id={`derived-value-bool-${dataTestId ?? "value"}`}>{label}</InputLabel>
                <Select
                    labelId={`derived-value-bool-${dataTestId ?? "value"}`}
                    label={label}
                    value={value === true ? "true" : value === false ? "false" : ""}
                    onChange={(e) => {
                        const v = e.target.value;
                        if (v === "true") onChange(true);
                        else if (v === "false") onChange(false);
                        else onChange(undefined);
                    }}
                    displayEmpty
                    data-testid={dataTestId}
                >
                    <MenuItem value="">
                        <em>Select</em>
                    </MenuItem>
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                </Select>
            </FormControl>
        );
    }

    if (valueType === "NUMBER") {
        return (
            <TextField
                size={size}
                fullWidth={fullWidth}
                disabled={disabled}
                sx={sx}
                label={label}
                placeholder="Number"
                type="number"
                value={localInput}
                onChange={(e) => setLocalInput(e.target.value)}
                onBlur={() => commit(localInput)}
                inputProps={{ "data-testid": dataTestId }}
            />
        );
    }

    return (
        <Autocomplete
            freeSolo
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            sx={sx}
            options={uniqueByDisplay.map((o) => o.display)}
            value={displayStr}
            inputValue={localInput}
            onInputChange={(_, inputValue) => setLocalInput(inputValue ?? "")}
            onChange={(_, newValue) => {
                if (newValue == null) return;
                const opt = uniqueByDisplay.find((o) => o.display === newValue);
                if (opt) {
                    onChange(opt.raw);
                } else {
                    commit(String(newValue));
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    data-testid={dataTestId}
                    onBlur={() => commit(localInput)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            const target = e.target as HTMLInputElement;
                            commit(target?.value ?? "");
                        }
                    }}
                />
            )}
        />
    );
};
